import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import MainLayout from '../../components/layout/MainLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft,
  FileSignature,
  User,
  Building2,
  Euro,
  Calendar,
  Loader2,
  Save,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  Send,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const MemberContractCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  
  const preselectedMemberId = searchParams.get('member_id');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    member_id: preselectedMemberId || '',
    contract_type: 'monthly',
    pricing_type: 'hourly',
    hourly_rate: '',
    fixed_amount: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    brief: '',
    deadline: '',
    budget_hours: '',
    conditions: '',
    prestataire_statut: 'Micro-entrepreneur',
    prestataire_siret: '',
    prestataire_address: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (preselectedMemberId && members.length > 0) {
      const member = members.find(m => m.id === preselectedMemberId);
      if (member) {
        setSelectedMember(member);
        setFormData(prev => ({
          ...prev,
          member_id: member.id,
          hourly_rate: member.hourly_rate || '20',
          prestataire_siret: member.siret || '',
          prestataire_address: member.address || ''
        }));
      }
    }
  }, [preselectedMemberId, members]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, affiliatesRes, agencyRes] = await Promise.all([
        api.get('/users/team-members'),
        api.get('/users/?role=affiliate'),
        api.get('/settings/agency')
      ]);
      
      // Combine team members and affiliates
      const allMembers = [
        ...(membersRes.data || []).map(m => ({ ...m, member_type: m.member_type || 'team_member' })),
        ...(affiliatesRes.data || []).map(m => ({ ...m, member_type: 'affiliate' }))
      ];
      setMembers(allMembers);
      setAgencyInfo(agencyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (memberId) => {
    const member = members.find(m => m.id === memberId);
    setSelectedMember(member);
    
    // Auto-select contract type based on member type
    let contractType = formData.contract_type;
    if (member?.member_type === 'affiliate' || member?.role === 'affiliate') {
      contractType = 'affiliate';
    }
    
    setFormData(prev => ({
      ...prev,
      member_id: memberId,
      contract_type: contractType,
      hourly_rate: member?.hourly_rate || prev.hourly_rate,
      prestataire_siret: member?.siret || '',
      prestataire_address: member?.address || ''
    }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.member_id) {
      toast.error('Veuillez sélectionner un membre');
      return;
    }
    
    if (formData.pricing_type === 'hourly' && !formData.hourly_rate) {
      toast.error('Veuillez renseigner le taux horaire');
      return;
    }
    
    if (formData.pricing_type === 'fixed' && !formData.fixed_amount) {
      toast.error('Veuillez renseigner le montant forfaitaire');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        member_id: formData.member_id,
        contract_type: formData.contract_type,
        pricing_type: formData.pricing_type,
        hourly_rate: formData.pricing_type === 'hourly' ? parseFloat(formData.hourly_rate) : null,
        fixed_amount: formData.pricing_type === 'fixed' ? parseFloat(formData.fixed_amount) : null,
        conditions: formData.conditions || null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        brief: formData.brief || null,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        budget_hours: formData.budget_hours ? parseFloat(formData.budget_hours) : null,
        budget_amount: formData.pricing_type === 'hourly' && formData.budget_hours && formData.hourly_rate 
          ? parseFloat(formData.budget_hours) * parseFloat(formData.hourly_rate) 
          : (formData.pricing_type === 'fixed' ? parseFloat(formData.fixed_amount) : null),
        prestataire_statut: formData.prestataire_statut || null,
        prestataire_siret: formData.prestataire_siret || null,
        prestataire_address: formData.prestataire_address || null
      };

      await api.post('/member-contracts/', payload);
      toast.success('Contrat créé et envoyé au membre pour signature');
      navigate('/member-contracts');
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du contrat');
    } finally {
      setSaving(false);
    }
  };

  const legalStatusLabels = {
    auto_entrepreneur: 'Auto-entrepreneur',
    micro_entreprise: 'Micro-entreprise',
    eurl: 'EURL',
    sasu: 'SASU',
    sarl: 'SARL',
    sas: 'SAS',
    ei: 'Entreprise Individuelle',
    portage: 'Portage salarial'
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                Nouveau contrat membre
              </h1>
              <p className="text-slate-500 text-sm">
                Créez un contrat de prestation pour un membre de l'équipe
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSignature className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                  Informations du contrat
                </CardTitle>
                <CardDescription>
                  Remplissez les informations pour créer le contrat
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Member Selection */}
                  <div className="space-y-2">
                    <Label>Membre / Prestataire / Apporteur *</Label>
                    <Select 
                      value={formData.member_id} 
                      onValueChange={handleMemberSelect}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un membre..." />
                      </SelectTrigger>
                      <SelectContent>
                        {members.filter(m => m.member_type !== 'affiliate').length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500">Membres équipe</div>
                            {members.filter(m => m.member_type !== 'affiliate').map(member => (
                              <SelectItem key={member.id} value={member.id}>
                                <div className="flex items-center gap-2">
                                  <span>{member.first_name} {member.last_name}</span>
                                  {member.siret ? (
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">SIRET</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">Sans SIRET</Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {members.filter(m => m.member_type === 'affiliate').length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 mt-2 border-t pt-2">Apporteurs d'affaires</div>
                            {members.filter(m => m.member_type === 'affiliate').map(member => (
                              <SelectItem key={member.id} value={member.id}>
                                <div className="flex items-center gap-2">
                                  <span>{member.first_name} {member.last_name}</span>
                                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">Apporteur</Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Contract Type */}
                  <div className="space-y-2">
                    <Label>Type de contrat *</Label>
                    <Select 
                      value={formData.contract_type} 
                      onValueChange={(v) => updateField('contract_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            <span>Créatif (récurrent)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="mission">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span>Sous-traitant (mission ponctuelle)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="affiliate">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>Apporteur d'affaires (commission)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pricing Type - Only for mission contracts */}
                  {formData.contract_type === 'mission' && (
                    <div className="space-y-2">
                      <Label>Mode de tarification *</Label>
                      <Select 
                        value={formData.pricing_type} 
                        onValueChange={(v) => updateField('pricing_type', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Taux horaire</SelectItem>
                          <SelectItem value="fixed">Forfait</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Pricing fields */}
                  {(formData.contract_type === 'monthly' || formData.pricing_type === 'hourly') && (
                    <div className="space-y-2">
                      <Label>Taux horaire (€/h) *</Label>
                      <Input
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => updateField('hourly_rate', e.target.value)}
                        placeholder="Ex: 45"
                      />
                    </div>
                  )}

                  {formData.contract_type === 'mission' && formData.pricing_type === 'fixed' && (
                    <div className="space-y-2">
                      <Label>Montant forfaitaire (€ HT) *</Label>
                      <Input
                        type="number"
                        value={formData.fixed_amount}
                        onChange={(e) => updateField('fixed_amount', e.target.value)}
                        placeholder="Ex: 1500"
                      />
                      <p className="text-xs text-slate-500">Montant global pour la mission</p>
                    </div>
                  )}

                  {/* Dates */}
                  {formData.contract_type === 'monthly' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date de début</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => updateField('start_date', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date de fin (optionnel)</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => updateField('end_date', e.target.value)}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Mission specific fields */}
                      <div className="space-y-2">
                        <Label>Brief de la prestation *</Label>
                        <Textarea
                          value={formData.brief}
                          onChange={(e) => updateField('brief', e.target.value)}
                          placeholder="Décrivez la prestation demandée..."
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Deadline</Label>
                        <Input
                          type="date"
                          value={formData.deadline}
                          onChange={(e) => updateField('deadline', e.target.value)}
                        />
                      </div>

                      {formData.pricing_type === 'hourly' && (
                        <div className="space-y-2">
                          <Label>Budget heures</Label>
                          <Input
                            type="number"
                            value={formData.budget_hours}
                            onChange={(e) => updateField('budget_hours', e.target.value)}
                            placeholder="Ex: 10"
                          />
                          {formData.budget_hours && formData.hourly_rate && (
                            <p className="text-sm text-slate-600">
                              Budget total : <span className="font-semibold">
                                {(parseFloat(formData.budget_hours) * parseFloat(formData.hourly_rate)).toFixed(2)} € HT
                              </span>
                              <span className="text-slate-400 ml-1">
                                ({formData.budget_hours}h × {formData.hourly_rate}€/h)
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Conditions */}
                  <div className="space-y-2">
                    <Label>Conditions particulières</Label>
                    <Textarea
                      value={formData.conditions}
                      onChange={(e) => updateField('conditions', e.target.value)}
                      placeholder="Conditions spécifiques du contrat..."
                      rows={2}
                    />
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => navigate(-1)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={saving}
                      className="flex-1"
                      style={{ backgroundColor: theme.colors?.accent }}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Création...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Créer et envoyer au membre
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Member Info */}
          <div className="space-y-6">
            {selectedMember ? (
              <>
                {/* Member Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Prestataire
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-center pb-3 border-b">
                      <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-2">
                        <span className="text-xl font-bold text-slate-600">
                          {selectedMember.first_name?.[0]}{selectedMember.last_name?.[0]}
                        </span>
                      </div>
                      <h3 className="font-semibold">
                        {selectedMember.first_name} {selectedMember.last_name}
                      </h3>
                      <p className="text-sm text-slate-500">{selectedMember.job_title}</p>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Statut</span>
                        <span>{legalStatusLabels[selectedMember.legal_status] || 'Non renseigné'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">SIRET</span>
                        <span className={selectedMember.siret ? 'font-mono text-xs' : 'text-amber-600'}>
                          {selectedMember.siret || 'Non renseigné'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Taux horaire</span>
                        <span>{selectedMember.hourly_rate ? `${selectedMember.hourly_rate}€/h` : '-'}</span>
                      </div>
                    </div>
                    
                    {!selectedMember.siret && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Ce membre n'a pas de SIRET renseigné
                        </p>
                        <Button 
                          variant="link" 
                          className="p-0 h-auto text-xs text-amber-700 underline"
                          onClick={() => navigate(`/users/${selectedMember.id}/edit`)}
                        >
                          Compléter son profil
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Agency Card */}
                {agencyInfo && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Agence (donneur d'ordre)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Nom</span>
                        <span>{agencyInfo.name || 'MSC'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">SIRET</span>
                        <span className="font-mono text-xs">{agencyInfo.siret || 'Non renseigné'}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <User className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">
                    Sélectionnez un membre pour voir ses informations
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Help Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <h4 className="font-medium text-blue-900 mb-2">💡 Conseil</h4>
                <p className="text-sm text-blue-700">
                  Le contrat sera automatiquement envoyé au membre pour signature. 
                  Une fois signé par les deux parties, il devient actif.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MemberContractCreatePage;
